import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { db, safeHttpsCallable } from '@/lib/firebase';
import { Brand, Drink, DrinkFlavor, DrinkAttribute, Confirmation } from '@/types/firestore';

// Define the possible types of entities the editor can interact with
// Moved type import above
// export type EditableEntityType = 'brand' | 'drink' | 'flavor' | 'attribute' | 'location' | 'sighting' | 'review' | 'user';
// Restore local definition
export type EditableEntityType = 'brand' | 'drink' | 'flavor' | 'attribute' | 'location' | 'sighting' | 'review' | 'user' | 'confirmation' | 'customMap' | 'visitedLocationNote';

// Type for loading status
type LoadingStatus = 'idle' | 'pending' | 'succeeded' | 'failed';

// Define the type for the deletion impact report that comes from the server
interface DeletionImpactReport {
  primaryDocument: {
    type: string;
    id: string;
    name: string;
  };
  dependentDocuments: {
    [collectionName: string]: {
      count: number;
      sampleItems?: Array<{id: string, name: string}>;
    }
  };
  totalImpactedDocuments: number;
}

// State for deletion impact assessment
interface DeletionImpact {
  loading: LoadingStatus;
  error: string | null;
  // Stores the deletion impact report
  report: DeletionImpactReport | null; 
}

// Main state shape for the editor slice
interface EditorState {
  interactionState: 'idle' | 'editing' | 'assessing_deletion' | 'confirming_deletion';
  selectedEntityType: EditableEntityType | null;
  selectedEntityId: string | null;
  selectedAuthorUserId: string | null;
  deletionImpact: DeletionImpact;
  // General error state for editor operations (e.g., final delete failure)
  operationLoading: LoadingStatus;
  operationError: string | null;
}

const initialDeletionImpactState: DeletionImpact = {
    loading: 'idle',
    error: null,
    report: null,
};

const initialState: EditorState = {
  interactionState: 'idle',
  selectedEntityType: null,
  selectedEntityId: null,
  selectedAuthorUserId: null,
  deletionImpact: initialDeletionImpactState,
  operationLoading: 'idle',
  operationError: null,
};

// --- Async Thunks ---

// Thunk to assess deletion impact
export const assessDeletionImpact = createAsyncThunk<
    DeletionImpactReport, // Return type: the impact report
    { entityType: EditableEntityType; entityId: string }, // Argument type
    { rejectValue: string } // Thunk config
>(
    'editor/assessDeletionImpact',
    async ({ entityType, entityId }, { rejectWithValue }) => {
        console.log(`[EditorSlice] Assessing deletion impact for ${entityType} ID: ${entityId}`);
        try {
            // Skip assessment for visitedLocationNote as the impact is just the note itself
            if (entityType === 'visitedLocationNote') {
                return { 
                  primaryDocument: {
                    type: 'visitedLocationNote',
                    id: entityId,
                    name: 'Public Note'
                  },
                  dependentDocuments: {},
                  totalImpactedDocuments: 1
                }; // Return simple report
            }
            // Use safeHttpsCallable
            const result = await safeHttpsCallable('assessDeletionImpact', { 
                // Use the parameter names expected by the Cloud Function
                documentType: entityType,
                documentId: entityId 
            });
            
            // Check if we got a valid impact report
            if (result.data && typeof result.data === 'object') {
                console.log("Received impact assessment:", result.data);
                return result.data as DeletionImpactReport;
            } else {
                 throw new Error('Invalid report format received from cloud function.');
            }
        } catch (error: any) {
            console.error("Error assessing deletion impact:", error);
            return rejectWithValue(error.message || 'Failed to assess deletion impact');
        }
    }
);

// Define a more specific argument type for deleteEntity
interface DeleteEntityArgs {
  entityType: EditableEntityType;
  entityId: string;
  sightingId?: string; // Optional: only used for confirmations
  authorUserId?: string; // Optional: only used for visitedLocationNote
}

// Thunk to delete an entity (Callable Cloud Function or direct Firestore for notes)
export const deleteEntity = createAsyncThunk<
  void,
  DeleteEntityArgs, // Use the updated args type
  { rejectValue: string; state: RootState } 
>(
  'editor/deleteEntity',
  // Destructure authorUserId from args here
  async ({ entityType, entityId, authorUserId, sightingId }, { rejectWithValue, getState }) => {
    console.log(`[EditorSlice] Deleting entity: ${entityType}, ID: ${entityId}${sightingId ? `, sightingId: ${sightingId}` : ''}${authorUserId ? `, authorUserId: ${authorUserId}` : ''}`);
    
    // Handle visitedLocationNote deletion directly
    if (entityType === 'visitedLocationNote') {
      // Get authorId and locationId from the arguments passed to the thunk,
      // no need to rely on potentially stale state here.
      if (!authorUserId) {
          return rejectWithValue('Author User ID is required for note deletion.');
      }
      if (!entityId) { // entityId here represents the locationId
          return rejectWithValue('Location ID is required for note deletion.');
      }

      try {
        const noteDocRef = doc(db, `users/${authorUserId}/visitedLocations`, entityId);
        // Update the publicNote field to null to "delete" it
        await updateDoc(noteDocRef, { publicNote: null }); 
        return; // Indicate success
      } catch (error: any) {
        console.error(`Error deleting public note for user ${authorUserId} at location ${entityId}:`, error);
        return rejectWithValue(error.message || 'Failed to delete public note');
      }
    }

    // Handle other entities via Cloud Function
    try {
      // Get the deletion impact assessment from the state
      const state = getState();
      const impactReport = state.editor.deletionImpact.report;
      
      if (!impactReport && entityType !== 'confirmation') {
        return rejectWithValue('Missing impact assessment. Please try again.');
      }
      
      const confirmedImpact = entityType === 'confirmation' ? 1 : 
        (impactReport?.totalImpactedDocuments || 0);
      
      // Call the cloud function with the appropriate parameter names
      await safeHttpsCallable('executeCascadingDeletion', { 
        documentType: entityType, 
        documentId: entityId,
        sightingId: sightingId, // Pass sightingId if provided (for confirmations)
        confirmedImpact: confirmedImpact
      });
      
      // If successful, the fulfilled case will be triggered
      return;
    } catch (error: any) {
      console.error(`Error deleting ${entityType} ${entityId}:`, error);
      
      // Try to extract the error message from Firebase error object
      const errorMessage = error.message || 
        (error.details ? JSON.stringify(error.details) : 'Failed to delete entity');
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Thunk to add a new Brand
export const addBrand = createAsyncThunk<
    string, // Return the new brand ID
    { name: string; brandSize: Brand['brandSize'] }, // Argument type
    { rejectValue: string } // Thunk config
>(
    'editor/addBrand',
    async (brandData, { rejectWithValue }) => {
        console.log('[EditorSlice] Adding brand:', brandData);
        try {
            const brandsCollection = collection(db, 'brands');
            const docRef = await addDoc(brandsCollection, brandData);
            return docRef.id; // Return the new document ID
        } catch (error: any) {
            console.error("Error adding brand:", error);
            return rejectWithValue(error.message || 'Failed to add brand');
        }
    }
);

// Thunk to add a new Drink
export const addDrink = createAsyncThunk<
    string, // Return the new drink ID
    { name: string; brandId: string; brandName: string; brandSize: Brand['brandSize']; flavors: DrinkFlavor[]; attributes: DrinkAttribute[] }, // Argument type matching useDrinks hook
    { rejectValue: string } // Thunk config
>(
    'editor/addDrink',
    async (drinkData, { rejectWithValue }) => {
        console.log('[EditorSlice] Adding drink:', drinkData);
        // Add default fields expected by the Drink type but not provided in args
        const fullDrinkData: Omit<Drink, 'id'> = {
            ...drinkData,
            ratingSum: 0,
            averageRating: 0,
            reviewCount: 0,
            publicReviewCount: 0,
            sightingCount: 0,
        };
        try {
            const drinksCollection = collection(db, 'drinks');
            const docRef = await addDoc(drinksCollection, fullDrinkData);
            return docRef.id; // Return the new document ID
        } catch (error: any) {
            console.error("Error adding drink:", error);
            return rejectWithValue(error.message || 'Failed to add drink');
        }
    }
);

// Thunk to update a Drink
export const updateDrink = createAsyncThunk<
    void, // Return void on success
    { id: string; data: Partial<Drink> }, // Argument: drink ID and partial data
    { rejectValue: string } // Thunk config
>(
    'editor/updateDrink',
    async ({ id, data }, { rejectWithValue }) => {
        console.log(`[EditorSlice] Updating drink ${id}:`, data);
        try {
            const drinkDocRef = doc(db, 'drinks', id);
            await updateDoc(drinkDocRef, data);
            // No return needed, success implied
        } catch (error: any) {
            console.error(`Error updating drink ${id}:`, error);
            return rejectWithValue(error.message || 'Failed to update drink');
        }
    }
);

// Thunk to update a Brand
export const updateBrand = createAsyncThunk<
    void, // Return void on success
    { id: string; data: Partial<Brand> }, // Argument: brand ID and partial data
    { rejectValue: string } // Thunk config
>(
    'editor/updateBrand',
    async ({ id, data }, { rejectWithValue }) => {
        console.log(`[EditorSlice] Updating brand ${id}:`, data);
        try {
            const brandDocRef = doc(db, 'brands', id);
            await updateDoc(brandDocRef, data);
            // No return needed, success implied
        } catch (error: any) {
            console.error(`Error updating brand ${id}:`, error);
            return rejectWithValue(error.message || 'Failed to update brand');
        }
    }
);

// Thunk to update a Review
export const updateReview = createAsyncThunk<
    void, // Return void on success
    { id: string; data: { reviewText?: string; rating?: number; isPublic?: boolean } }, // Argument: review ID and updateable fields
    { rejectValue: string } // Thunk config
>(
    'editor/updateReview',
    async ({ id, data }, { rejectWithValue }) => {
        console.log(`[EditorSlice] Updating review ${id}:`, data);
        try {
            const reviewDocRef = doc(db, 'reviews', id);
            await updateDoc(reviewDocRef, data);
            // No return needed, success implied
        } catch (error: any) {
            console.error(`Error updating review ${id}:`, error);
            return rejectWithValue(error.message || 'Failed to update review');
        }
    }
);

// Thunk to update a Confirmation
export const updateConfirmation = createAsyncThunk<
  void,
  { sightingId: string; confirmationId: string; data: Partial<Confirmation> },
  { rejectValue: string }
>(
  'editor/updateConfirmation',
  async ({ sightingId, confirmationId, data }, { rejectWithValue }) => {
    if (!sightingId || !confirmationId) {
        return rejectWithValue('Sighting ID and Confirmation ID are required to update confirmation.');
    }
    console.log(`[EditorSlice] Updating confirmation ${confirmationId} in sighting ${sightingId}:`, data);
    try {
      const confirmationDocRef = doc(db, 'sightings', sightingId, 'confirmations', confirmationId);
      await updateDoc(confirmationDocRef, data);
    } catch (error: any) {
      console.error(`Error updating confirmation ${confirmationId}:`, error);
      return rejectWithValue(error.message || 'Failed to update confirmation.');
    }
  }
);

// --- Slice Definition ---
const editorSlice = createSlice({
    name: 'editor',
    initialState,
    reducers: {
        startEditInteraction: (state, action: PayloadAction<{ entityType: EditableEntityType; entityId: string }>) => {
            // Cannot edit visitedLocationNote via this flow
            if (action.payload.entityType === 'visitedLocationNote') {
                console.warn('Attempted to edit visitedLocationNote via standard editor flow. Ignoring.');
                return; 
            }
            state.interactionState = 'editing';
            state.selectedEntityType = action.payload.entityType;
            state.selectedEntityId = action.payload.entityId;
            state.selectedAuthorUserId = null; // Ensure author ID is cleared
            state.deletionImpact = initialDeletionImpactState; // Clear deletion impact
            state.operationError = null;
        },
        startDeletionAssessment: (state, action: PayloadAction<{ 
            entityType: EditableEntityType; 
            entityId: string;
            authorUserId?: string;
        }>) => {
            state.selectedEntityType = action.payload.entityType;
            state.selectedEntityId = action.payload.entityId;
            state.selectedAuthorUserId = action.payload.authorUserId || null; // Store authorUserId if provided
            state.operationLoading = 'idle';
            state.operationError = null;
            
            // Always show the deletion dialog immediately, but set different states for assessment
            state.interactionState = 'confirming_deletion';
            
            // Skip impact assessment for simple entities, provide immediate report
            if (action.payload.entityType === 'visitedLocationNote' || 
                action.payload.entityType === 'confirmation' ||
                action.payload.entityType === 'review') {
                state.deletionImpact.loading = 'succeeded'; // Mark assessment as (skipped) succeeded
                state.deletionImpact.report = {
                  primaryDocument: {
                    type: action.payload.entityType,
                    id: action.payload.entityId,
                    name: action.payload.entityType === 'visitedLocationNote' 
                        ? 'Public Note' 
                        : action.payload.entityType === 'review'
                        ? 'Review'
                        : 'Confirmation'
                  },
                  dependentDocuments: {},
                  totalImpactedDocuments: 1
                }; // Minimal valid report
            } else {
                // For complex entities, start with loading state but show dialog
                state.deletionImpact.loading = 'pending'; // Set loading for impact assessment
                state.deletionImpact.error = null;
                state.deletionImpact.report = null;
            }
        },
        cancelInteraction: (state) => {
            state.interactionState = 'idle';
            state.selectedEntityType = null;
            state.selectedEntityId = null;
            state.selectedAuthorUserId = null; // Clear author ID
            state.deletionImpact = initialDeletionImpactState;
            state.operationLoading = 'idle';
            state.operationError = null;
        },
        // Internal reducer to reset operation status (might be called by thunks)
        resetOperationStatus: (state) => {
             state.operationLoading = 'idle';
             state.operationError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // assessDeletionImpact
            .addCase(assessDeletionImpact.pending, (state) => {
                state.deletionImpact.loading = 'pending';
                state.deletionImpact.error = null;
                state.deletionImpact.report = null;
                state.interactionState = 'assessing_deletion'; // Ensure state is correct
            })
            .addCase(assessDeletionImpact.fulfilled, (state, action) => {
                state.deletionImpact.loading = 'succeeded';
                state.deletionImpact.report = action.payload;
                state.interactionState = 'confirming_deletion'; // Move to confirmation step
            })
            .addCase(assessDeletionImpact.rejected, (state, action) => {
                state.deletionImpact.loading = 'failed';
                state.deletionImpact.error = action.payload ?? 'Failed assessment';
                state.interactionState = 'idle'; // Revert to idle on assessment failure
                state.selectedEntityType = null; // Clear selection on error
                state.selectedEntityId = null;
            })
            // deleteEntity
            .addCase(deleteEntity.pending, (state) => {
                state.operationLoading = 'pending';
                state.operationError = null;
                // Keep interactionState as confirming_deletion during pending?
            })
            .addCase(deleteEntity.fulfilled, (state) => {
                // Deletion successful, reset state
                editorSlice.caseReducers.cancelInteraction(state);
                // Note: Data removal from other slices (e.g., locations) is handled by listeners or those slices' thunks
            })
            .addCase(deleteEntity.rejected, (state, action) => {
                state.operationLoading = 'failed';
                state.operationError = action.payload ?? 'Deletion failed';
                // Keep state as confirming_deletion to show error in modal?
                // Or revert to idle? Let's revert for now.
                // editorSlice.caseReducers.cancelInteraction(state);
                // Maybe just set error and let UI decide to call cancelInteraction
                state.interactionState = 'confirming_deletion'; // Stay here to show error
            })
            // addBrand
            .addCase(addBrand.pending, (state) => {
                state.operationLoading = 'pending';
                state.operationError = null;
            })
            .addCase(addBrand.fulfilled, (state) => {
                state.operationLoading = 'succeeded';
                // Data update handled by globalDataSlice listener
            })
            .addCase(addBrand.rejected, (state, action) => {
                state.operationLoading = 'failed';
                state.operationError = action.payload ?? 'Failed to add brand';
            })
            // addDrink
            .addCase(addDrink.pending, (state) => {
                state.operationLoading = 'pending';
                state.operationError = null;
            })
            .addCase(addDrink.fulfilled, (state) => {
                state.operationLoading = 'succeeded';
                // Data update handled by globalDataSlice listener
            })
            .addCase(addDrink.rejected, (state, action) => {
                state.operationLoading = 'failed';
                state.operationError = action.payload ?? 'Failed to add drink';
            })
            // updateDrink
            .addCase(updateDrink.pending, (state) => {
                state.operationLoading = 'pending';
                state.operationError = null;
            })
            .addCase(updateDrink.fulfilled, (state) => {
                state.operationLoading = 'succeeded';
                // Listener in globalDataSlice should handle the update
            })
            .addCase(updateDrink.rejected, (state, action) => {
                state.operationLoading = 'failed';
                state.operationError = action.payload ?? 'Failed to update drink';
            })
            // updateBrand
            .addCase(updateBrand.pending, (state) => {
                state.operationLoading = 'pending';
                state.operationError = null;
            })
            .addCase(updateBrand.fulfilled, (state) => {
                state.operationLoading = 'succeeded';
                // Listener in globalDataSlice should handle the update
            })
            .addCase(updateBrand.rejected, (state, action) => {
                state.operationLoading = 'failed';
                state.operationError = action.payload ?? 'Failed to update brand';
            })
            // updateReview
            .addCase(updateReview.pending, (state) => {
                state.operationLoading = 'pending';
                state.operationError = null;
            })
            .addCase(updateReview.fulfilled, (state) => {
                state.operationLoading = 'succeeded';
                // Listener in globalDataSlice should handle the update
            })
            .addCase(updateReview.rejected, (state, action) => {
                state.operationLoading = 'failed';
                state.operationError = action.payload ?? 'Failed to update review';
            })
            // updateConfirmation
            .addCase(updateConfirmation.pending, (state) => {
                state.operationLoading = 'pending';
                state.operationError = null;
            })
            .addCase(updateConfirmation.fulfilled, (state) => {
                state.operationLoading = 'succeeded';
                // Consider if interactionState needs to change or if cancelInteraction should be called
            })
            .addCase(updateConfirmation.rejected, (state, action) => {
                state.operationLoading = 'failed';
                state.operationError = action.payload ?? 'Failed to update confirmation';
            });
    },
});

// Export actions and reducer
export const { 
    startEditInteraction, 
    startDeletionAssessment, 
    cancelInteraction,
    resetOperationStatus
} = editorSlice.actions;
export default editorSlice.reducer;

// --- Selectors ---
export const selectEditorState = (state: RootState) => state.editor;
export const selectEditorInteractionState = (state: RootState) => state.editor.interactionState;

// Define the type for the selected entity object
interface SelectedEntityInfo {
    type: EditableEntityType;
    id: string;
    authorUserId?: string | null; // Make authorUserId optional
}

// Derive selected entity details for components
export const selectSelectedEntity = (state: RootState): SelectedEntityInfo | null => {
    if (!state.editor.selectedEntityType || !state.editor.selectedEntityId) {
        return null;
    }
    const selectedEntity: SelectedEntityInfo = {
        type: state.editor.selectedEntityType,
        id: state.editor.selectedEntityId,
    };
    // Include authorUserId only when the entity type is visitedLocationNote
    if (state.editor.selectedEntityType === 'visitedLocationNote') {
        selectedEntity.authorUserId = state.editor.selectedAuthorUserId;
    }
    return selectedEntity;
};

export const selectDeletionImpact = (state: RootState) => state.editor.deletionImpact;
export const selectEditorOperationLoading = (state: RootState) => state.editor.operationLoading;
export const selectEditorOperationError = (state: RootState) => state.editor.operationError; 